import { formatSAR } from "@/utils/format";
import type { OrderRecord } from "@/types";

interface OrderDebtAlertProps {
  order: OrderRecord;
}

/**
 * Explains the customer wallet debt state for an order that came from the
 * customer app and was paid in cash. The charged amount is the value set by
 * the app admin for the store category (not necessarily the full order value).
 * Only app orders can carry a customer wallet debt — POS/ cashier orders cannot.
 */
export default function OrderDebtAlert({ order }: OrderDebtAlertProps) {
  const { status, debtRecorded } = order;
  // The debt value is set by the app admin for the store category and is not
  // necessarily the full invoice total.
  const debtAmount = order.debtAmount ?? order.amount;

  // Customer wallet debt applies only to cash orders created from the customer app.
  if (order.source !== "app") return null;
  if (order.payment !== "cash" || status === "received") return null;

  const customerDidNotReceive = status === "missed";
  const walletBalance = order.customerWalletBalance ?? 0;
  const paidFromWallet = customerDidNotReceive && walletBalance >= debtAmount;
  const negativeBalance =
    customerDidNotReceive && walletBalance < debtAmount
      ? Math.round((debtAmount - walletBalance) * 100) / 100
      : 0;
  const totalDebt = order.customerTotalDebt ?? debtAmount;

  // Still in progress: just explain where the customer debt stands right now.
  if (!customerDidNotReceive) {
    return (
      <div className="rounded-card border border-background-200 bg-background-50 p-5">
        <div className="flex items-start gap-3">
          <span
            className={`w-9 h-9 shrink-0 flex items-center justify-center rounded-full text-lg ${
              debtRecorded
                ? "bg-accent-500 text-white"
                : "bg-background-200 text-foreground-600"
            }`}
          >
            <i
              className={debtRecorded ? "ri-time-line" : "ri-hourglass-line"}
            ></i>
          </span>
          <div className="min-w-0">
            <h3
              className={`text-sm font-bold ${
                debtRecorded ? "text-accent-900" : "text-foreground-700"
              }`}
            >
              {debtRecorded
                ? "مديونية العميل مُسجّلة على هذا الطلب"
                : "لم تُسجَّل مديونية العميل بعد"}
            </h3>
            <p className="mt-2 text-[11px] text-foreground-500">
              قيمة المديونية المحتسبة وفق إعدادات قسم المتجر
            </p>
            <p className="mt-0.5 text-sm font-semibold text-foreground-900">
              {formatSAR(debtAmount)}
            </p>
            <p className="mt-1 text-xs leading-6 text-foreground-500">
              {debtRecorded
                ? "بانتظار استلام العميل. في حال عدم الاستلام تُخصم القيمة من محفظة العميل، أو تُسجَّل كرصيد سالب إذا كان الرصيد غير كافٍ."
                : "تُحتسب مديونية العميل على طلبات الدفع النقدي المنشأة من التطبيق في حال عدم استلامه للطلب."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-card border p-5 ${
        paidFromWallet
          ? "border-background-200 bg-background-50"
          : "border-accent-200 bg-accent-50"
      }`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`w-9 h-9 shrink-0 flex items-center justify-center rounded-full text-lg ${
            paidFromWallet
              ? "bg-accent-100 text-accent-700"
              : "bg-accent-500 text-white"
          }`}
        >
          <i
            className={paidFromWallet ? "ri-wallet-3-line" : "ri-alert-line"}
          ></i>
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3
              className={`text-sm font-bold ${
                paidFromWallet ? "text-foreground-900" : "text-accent-900"
              }`}
            >
              مديونية العميل —{" "}
              {paidFromWallet ? "خُصمت من المحفظة" : "رصيد سالب"}
            </h3>
            <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-2.5 py-0.5 text-[11px] font-semibold text-white whitespace-nowrap">
              <i className="ri-close-circle-line"></i>
              لم يستلم العميل
            </span>
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div
              className={`rounded-btn border px-3 py-2.5 ${
                paidFromWallet
                  ? "border-background-200 bg-background-100"
                  : "border-accent-200 bg-white/80"
              }`}
            >
              <p className="text-[11px] text-foreground-500">
                قيمة المديونية المحتسبة وفق إعدادات قسم المتجر
              </p>
              <p className="mt-0.5 text-sm font-bold text-foreground-900">
                {formatSAR(debtAmount)}
              </p>
            </div>
            <div
              className={`rounded-btn border px-3 py-2.5 ${
                paidFromWallet
                  ? "border-background-200 bg-background-100"
                  : "border-accent-200 bg-white/80"
              }`}
            >
              <p className="text-[11px] text-foreground-500">
                إجمالي الدين على العميل
              </p>
              <p className="mt-0.5 text-sm font-bold text-foreground-900">
                {formatSAR(totalDebt)}
              </p>
            </div>
          </div>

          <p className="mt-3 text-xs leading-6 text-foreground-600">
            {paidFromWallet
              ? `رصيد محفظة العميل كان ${formatSAR(walletBalance)}، وتم خصم ${formatSAR(debtAmount)} تلقائيًا لصالح التاجر — لا يوجد رصيد سالب.`
              : `رصيد محفظة العميل كان ${formatSAR(walletBalance)} وهو غير كافٍ، فتم تسجيل رصيد سالب بقيمة ${formatSAR(negativeBalance)}.`}
          </p>

          {!paidFromWallet && (
            <div className="mt-3 flex items-start gap-2 rounded-btn border border-accent-300 bg-accent-100/70 px-3 py-2.5">
              <i className="ri-error-warning-line text-accent-700 mt-0.5"></i>
              <p className="text-xs leading-6 text-accent-900">
                لا يمكن للعميل إنشاء طلب جديد أو الاستفادة من خدمات التطبيق حتى
                يسدّد المديونية المستحقة لصالح التاجر من المحفظة الرقمية.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}